import { filterCatalogQuests } from './_filter-quests'
import { MoreButton } from './_more'
import { CatalogFiltersData, Quest } from '../../types.d'
import { createQuestCardElement } from '../../_quest'
import { NotFoundMessage } from './_not-found'
import { CatalogAnalytics } from './_analytics'
import { rankClickQuests } from './_click-ranking'

const HELPER_CARD_POSITION = 12

export function getQuestPage(
  quests: Quest[],
  page: number,
  pageSize: number
): Quest[] {
  const start = (page - 1) * pageSize

  return quests.slice(start, start + pageSize)
}

// Catalog List TypeScript
// Квесты без рейтинга уходят в конец: parseFloat(null) даёт NaN, и порядок
// таких карточек становится случайным.
export function sortByPopularity(quests: Quest[]): Quest[] {
  return quests.sort((a: Quest, b: Quest) => {
    const left = a.rating ? parseFloat(a.rating) : -Infinity
    const right = b.rating ? parseFloat(b.rating) : -Infinity
    return right - left
  })
}

export function appendCatalogCards(
  fragment: DocumentFragment,
  quests: Quest[],
  helperCard: HTMLElement | null,
  append: boolean
): void {
  quests.forEach((quest, index) => {
    fragment.appendChild(createQuestCardElement(quest))

    if (!append && helperCard && index === HELPER_CARD_POSITION - 1) {
      fragment.appendChild(helperCard)
    }
  })
}

export function isRenderedPage(
  container: HTMLElement,
  quests: Quest[]
): boolean {
  const renderedIds = Array.from(
    container.querySelectorAll<HTMLElement>(':scope > [data-quest-id]'),
    (card) => card.dataset.questId
  )

  return (
    renderedIds.length === quests.length &&
    quests.every((quest, index) => String(quest.id) === renderedIds[index])
  )
}

export class CatalogList {
  private container: HTMLElement
  private filters: CatalogFiltersData
  // Должно совпадать с data-catalog-sort-value по умолчанию в разметке
  // (см. quests/index/catalog/_sort.html.erb), иначе первое же изменение
  // фильтра неожиданно пересортирует список квестов.
  private sort: string = 'default'
  private search: string = ''
  private quests: Quest[] = []
  private filteredQuests: Quest[] = []
  private filtersChangeHandler: (_filters: CatalogFiltersData) => void
  private searchCHangeHandler: (_search: string) => void
  private sortChangeHandler: (_sort: string) => void
  private showMoreClickHandler: () => boolean
  private moreBtn: MoreButton
  private totalPages: number = 1
  private currentPage: number = 1
  private notFoundMessage: NotFoundMessage
  private pageSize: number
  private helperCard: HTMLElement | null
  private onResultsChange?: (_quests: Quest[]) => void
  private analytics: CatalogAnalytics

  constructor(
    container: HTMLElement,
    quests: Quest[],
    pageSize: number,
    onResultsChange?: (_quests: Quest[]) => void
  ) {
    this.container = container
    this.pageSize = pageSize
    this.helperCard = this.container.querySelector('[data-helper-card]')
    this.onResultsChange = onResultsChange
    this.filters = this.getDefaultFilters()
    this.quests = quests
    this.filteredQuests = quests.slice()
    this.totalPages = Math.ceil(this.filteredQuests.length / this.pageSize)
    this.init()
    this.analytics = new CatalogAnalytics(this.container)
  }

  private init(): void {
    this.filtersChangeHandler = this.applyFilters.bind(this)
    this.searchCHangeHandler = this.searchQuests.bind(this)
    this.sortChangeHandler = this.applySort.bind(this)
    this.showMoreClickHandler = this.loadMoreQuests.bind(this)

    const moreButtonEl =
      this.container.parentElement!.querySelector<HTMLElement>(
        '[data-load-more]'
      )
    this.moreBtn = new MoreButton(
      moreButtonEl!,
      this.getShowMoreClickHandler(),
      this.totalPages > 1
    )

    const notFoundEl = this.container
      .closest('[data-quests-catalog]')!
      .querySelector<HTMLElement>('[data-not-found]')
    this.notFoundMessage = new NotFoundMessage(notFoundEl!)
  }

  public getFiltersChangeHandler() {
    return this.filtersChangeHandler
  }

  public getSearchChangeHandler() {
    return this.searchCHangeHandler
  }

  public getSortChangeHandler() {
    return this.sortChangeHandler
  }

  public getShowMoreClickHandler() {
    return this.showMoreClickHandler
  }

  private getDefaultFilters(): CatalogFiltersData {
    return {
      type: '',
      categories: [],
      horror: [],
      complexity: [],
      metro: [],
      age: ''
    }
  }

  private searchQuests(search: string): void {
    this.search = search
    this.applyFilters(this.filters)
  }

  private applyFilters(filters: CatalogFiltersData): void {
    this.filters = filters

    this.filteredQuests = filterCatalogQuests(this.quests, filters, this.search)

    this.totalPages = Math.ceil(this.filteredQuests.length / this.pageSize)
    this.currentPage = 1
    this.moreBtn.updateVisibility(this.totalPages > 1)

    this.sortQuests()
    this.onResultsChange?.(this.filteredQuests)
    this.renderQuests(
      getQuestPage(this.filteredQuests, 1, this.pageSize),
      false
    )

    this.notFoundMessage.updateVisibility(this.filteredQuests.length === 0)
  }

  private applySort(sortValue: string): void {
    this.sort = sortValue

    this.applyFilters(this.filters)
  }

  private sortQuests(): void {
    switch (this.sort) {
      case 'default':
        this.filteredQuests = rankClickQuests(this.filteredQuests)
        return
      case 'popular':
        sortByPopularity(this.filteredQuests)
        break
      case 'alphabet-desc':
        this.filteredQuests.sort((a: Quest, b: Quest) =>
          (a.name || '').localeCompare(b.name || '')
        )
        break
      case 'alphabet-asc':
        this.filteredQuests.sort((a: Quest, b: Quest) =>
          (b.name || '').localeCompare(a.name || '')
        )
        break
      default:
        console.warn(`Unknown sort option: ${this.sort}`)
        return
    }
  }

  private loadMoreQuests(): boolean {
    if (this.currentPage >= this.totalPages) return false

    this.currentPage += 1
    const nextQuests = getQuestPage(
      this.filteredQuests,
      this.currentPage,
      this.pageSize
    )
    this.renderQuests(nextQuests, true)

    return this.currentPage < this.totalPages
  }

  private renderQuests(quests: Quest[], append: boolean): void {
    // Первую страницу сервер уже отрисовал: если она совпадает, пересборка
    // только сбросила бы загруженные обложки, и плитка мигнула бы пустой.
    if (append || !isRenderedPage(this.container, quests)) {
      const listFragment = document.createDocumentFragment()
      appendCatalogCards(listFragment, quests, this.helperCard, append)

      if (!append) {
        this.container.innerHTML = ''
      }
      this.container.appendChild(listFragment)
    }
    this.analytics?.refresh(append, {
      sort: this.sort,
      filtered: Object.values(this.filters).some((value) => value.length > 0),
      searched: this.search.trim().length > 0
    })
  }
}
